import { useState, useEffect } from 'react';

const API = 'https://urbankdcni.co.uk/api';
const UPLOADS = 'https://urbankdcni.co.uk/uploads';

const stageColors = {
  Survey:       { bg: '#1a1a2e', color: '#7b8cde' },
  Design:       { bg: '#1a2a1a', color: '#6dbf6d' },
  Installation: { bg: '#2a1a0e', color: '#F47820' },
  Complete:     { bg: '#0e2a1a', color: '#4ecb8d' },
};

function PhotoUpload({ projectId, token, onUploaded }) {
  const [caption, setCaption]     = useState('');
  const [file, setFile]           = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState('');

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    const form = new FormData();
    form.append('photo', file);
    form.append('project_id', projectId);
    form.append('caption', caption);
    try {
      const res = await fetch(`${API}/upload.php`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Upload failed');
      } else {
        setFile(null);
        setCaption('');
        onUploaded(data);
      }
    } catch (err) {
      setError('Could not connect');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={s.uploadBox}>
      <p style={s.uploadLabel}>Add a photo</p>
      <input
        type="file"
        accept="image/*"
        onChange={function(e) { setFile(e.target.files[0]); }}
        style={s.fileInput}
      />
      <input
        type="text"
        placeholder="Caption (optional)"
        value={caption}
        onChange={function(e) { setCaption(e.target.value); }}
        style={s.captionInput}
      />
      {error && <p style={s.errorText}>{error}</p>}
      <button
        onClick={handleUpload}
        disabled={!file || uploading}
        style={s.uploadBtn}
      >
        {uploading ? 'Uploading...' : 'Upload'}
      </button>
    </div>
  );
}

function ProjectCard({ project, token }) {
  const [photos, setPhotos]               = useState([]);
  const [expanded, setExpanded]           = useState(false);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [lightbox, setLightbox]           = useState(null);

  const stage = stageColors[project.stage] || stageColors.Survey;
  const badgeStyle = {
    fontSize: '12px',
    fontWeight: '600',
    padding: '4px 10px',
    borderRadius: '20px',
    whiteSpace: 'nowrap',
    background: stage.bg,
    color: stage.color,
  };

  const togglePhotos = function() {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (photos.length > 0) return;
    setLoadingPhotos(true);
    fetch(`${API}/photos.php?project_id=${project.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(function(r) { return r.json(); })
      .then(function(data) { setPhotos(data); setLoadingPhotos(false); })
      .catch(function() { setLoadingPhotos(false); });
  };

  const handleUploaded = function(newPhoto) {
    setPhotos(function(prev) { return [...prev, newPhoto]; });
  };

  const openLightbox = function(photo) { setLightbox(photo); };
  const closeLightbox = function() { setLightbox(null); };
  const downloadPhoto = function() {
    window.open(`${UPLOADS}/${lightbox.filename}`, '_blank');
  };

  return (
    <div style={s.card}>
      <div style={s.cardTop}>
        <h3 style={s.cardTitle}>{project.title}</h3>
        <span style={badgeStyle}>{project.stage}</span>
      </div>

      {project.notes && (
        <p style={s.cardNotes}>{project.notes}</p>
      )}

      <div style={s.cardFooter}>
        <p style={s.cardDate}>
          Created {new Date(project.created_at).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'long', year: 'numeric'
          })}
        </p>
        <button onClick={togglePhotos} style={s.photosBtn}>
          {expanded ? 'Hide photos' : 'Photos and uploads'}
        </button>
      </div>

      {expanded && (
        <div style={s.photoSection}>
          <PhotoUpload projectId={project.id} token={token} onUploaded={handleUploaded} />
          {loadingPhotos && <p style={s.muted}>Loading photos...</p>}
          <div style={s.photoGrid}>
            {photos.map(function(photo) {
              return (
                <div key={photo.id} style={s.photoItem} onClick={function() { openLightbox(photo); }}>
                  <img
                    src={`${UPLOADS}/${photo.filename}`}
                    alt={photo.caption || 'Project photo'}
                    style={s.photo}
                  />
                  {photo.caption && (
                    <p style={s.photoCaption}>{photo.caption}</p>
                  )}
                </div>
              );
            })}
          </div>
          {!loadingPhotos && photos.length === 0 && (
            <p style={s.muted}>No photos yet.</p>
          )}
        </div>
      )}

      {lightbox && (
        <div style={s.lightboxOverlay} onClick={closeLightbox}>
          <div style={s.lightboxInner} onClick={function(e) { e.stopPropagation(); }}>
            <img
              src={`${UPLOADS}/${lightbox.filename}`}
              alt={lightbox.caption || 'Project photo'}
              style={s.lightboxImg}
            />
            {lightbox.caption && (
              <p style={s.lightboxCaption}>{lightbox.caption}</p>
            )}
            <div style={s.lightboxButtons}>
              <button onClick={downloadPhoto} style={s.lightboxDownload}>
                Download
              </button>
              <button onClick={closeLightbox} style={s.lightboxClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const token = localStorage.getItem('uk_token');

  useEffect(function() {
    if (!token) { onLogout(); return; }
    fetch(`${API}/projects.php`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(function(res) {
        if (res.status === 401) { onLogout(); return null; }
        return res.json();
      })
      .then(function(data) {
        if (!data) return;
        setProjects(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(function() { setError('Could not load projects'); setLoading(false); });
  }, [token]);

  return (
    <div style={s.page}>
      <div style={s.container}>
        <div style={s.header}>
          <div>
            <h1 style={s.heading}>Urban Kitchens</h1>
            <p style={s.sub}>Welcome back, {user.name}</p>
          </div>
          <button onClick={onLogout} style={s.signout}>Sign out</button>
        </div>

        <h2 style={s.sectionTitle}>Your Projects</h2>

        {loading && <p style={s.muted}>Loading...</p>}
        {error && <p style={s.errorText}>{error}</p>}

        <div style={s.grid}>
          {projects.map(function(project) {
            return (
              <ProjectCard key={project.id} project={project} token={token} />
            );
          })}
        </div>

        {!loading && projects.length === 0 && (
          <p style={s.muted}>No projects yet — check back soon.</p>
        )}
      </div>
    </div>
  );
}

const s = {
  page:            { minHeight: '100vh', background: '#0f0f0f', color: '#fff', padding: '40px 20px' },
  container:       { maxWidth: '900px', margin: '0 auto' },
  header:          { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '48px' },
  heading:         { color: '#F47820', fontFamily: 'Bebas Neue, sans-serif', fontSize: '36px', margin: '0 0 4px' },
  sub:             { color: '#888', fontSize: '14px', margin: 0 },
  signout:         { background: 'transparent', border: '1px solid #333', color: '#888', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  sectionTitle:    { fontSize: '16px', fontWeight: '500', color: '#aaa', marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '0.05em' },
  grid:            { display: 'flex', flexDirection: 'column', gap: '16px' },
  card:            { background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '24px' },
  cardTop:         { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', gap: '12px' },
  cardTitle:       { fontSize: '18px', fontWeight: '500', color: '#fff', margin: 0 },
  cardNotes:       { color: '#999', fontSize: '14px', lineHeight: '1.6', margin: '0 0 12px' },
  cardFooter:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cardDate:        { color: '#555', fontSize: '12px', margin: 0 },
  photosBtn:       { background: 'transparent', border: '1px solid #333', color: '#aaa', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' },
  photoSection:    { marginTop: '24px', borderTop: '1px solid #2a2a2a', paddingTop: '24px' },
  photoGrid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginTop: '16px' },
  photoItem:       { display: 'flex', flexDirection: 'column', gap: '6px', cursor: 'pointer' },
  photo:           { width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '8px', background: '#111' },
  photoCaption:    { color: '#666', fontSize: '11px', margin: 0 },
  uploadBox:       { background: '#111', border: '1px dashed #333', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' },
  uploadLabel:     { color: '#aaa', fontSize: '13px', fontWeight: '500', margin: 0 },
  fileInput:       { color: '#aaa', fontSize: '13px' },
  captionInput:    { background: '#1a1a1a', border: '1px solid #333', borderRadius: '6px', padding: '8px 12px', color: '#fff', fontSize: '13px' },
  uploadBtn:       { background: '#F47820', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', alignSelf: 'flex-start' },
  muted:           { color: '#666', fontSize: '13px' },
  errorText:       { color: '#ff4444', fontSize: '13px' },
  lightboxOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  lightboxInner:   { maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' },
  lightboxImg:     { maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: '8px' },
  lightboxCaption: { color: '#ccc', fontSize: '14px', margin: 0 },
  lightboxButtons: { display: 'flex', gap: '10px' },
  lightboxDownload:{ background: '#F47820', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  lightboxClose:   { background: 'transparent', border: '1px solid #555', color: '#aaa', padding: '8px 20px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
};

export default Dashboard;